import {
  Outlet,
  createFileRoute,
  notFound,
  stripSearchParams,
} from '@tanstack/react-router'
import { Suspense } from 'react'
import * as v from 'valibot'

import { Center, Spinner } from '@masterplandev/ui'

import { CompanyPage } from '@/app/manager/CompanyPage/CompanyPage'
import { PageContainer } from '@/app/manager/Layout'
import {
  FEATURE_ENHANCED_DATA_TABLE,
  FEATURE_USER_GROUPS,
} from '@/core/constants/features'
import { TableSelectionProvider } from '@/core/contexts/TableSelection'
import { buildPageTitle } from '@/core/utils/buildPageTitle/buildPageTitle'
import useFeatures from '@/core/utils/features/useFeatures'
import { userGroupQueries } from '@/http/api/usersGroups'
import { UserGroupsList } from '@/user-groups/components/UserGroupsList/UserGroupsList'

const defaultQueryValues = {
  page: 0,
  size: 10,
  user_group_name: '',
  sort_by: 'creation_date' as const,
  sort_order: 'desc' as const,
  creator_id: undefined,
  creator_name: undefined,
  from_created_at: '',
  to_created_at: '',
  private: undefined,
  hidden_columns: [],
  column_order: [],
}

const paginationSchema = v.object({
  page: v.optional(v.number(), defaultQueryValues.page),
  size: v.optional(v.number(), defaultQueryValues.size),
})

const sortingSchema = v.object({
  sort_by: v.optional(
    v.union([
      v.literal('user_group_name'),
      v.literal('visibility'),
      v.literal('members'),
      v.literal('creation_date'),
    ]),
    defaultQueryValues.sort_by,
  ),
  sort_order: v.optional(
    v.union([v.literal('asc'), v.literal('desc')]),
    defaultQueryValues.sort_order,
  ),
})

const filterSchema = v.object({
  user_group_name: v.optional(v.string(), defaultQueryValues.user_group_name),
  private: v.optional(v.boolean(), defaultQueryValues.private),
  creator_id: v.optional(v.string(), defaultQueryValues.creator_id),
  creator_name: v.optional(v.string(), defaultQueryValues.creator_name),
  from_created_at: v.optional(v.string(), defaultQueryValues.from_created_at),
  to_created_at: v.optional(v.string(), defaultQueryValues.to_created_at),
})

const hiddenColumnIds = ['visibility', 'members', 'creation_date'] as const
const reorderableColumnIds = ['user_group_name', ...hiddenColumnIds] as const

export type UserGroupsHiddenColumn = (typeof hiddenColumnIds)[number]

// Drops unknown column ids, e.g. from links to columns that no longer exist
function knownColumnIds<Id extends string>(ids: readonly Id[]) {
  return v.pipe(
    v.array(v.string()),
    v.transform((values) =>
      values.filter((value): value is Id => ids.includes(value as Id)),
    ),
  )
}

const columnsSchema = v.object({
  // Columns the user has hidden. Absent from the URL means every column is
  // visible; "reset to default" clears this back to an empty list.
  hidden_columns: v.optional(
    knownColumnIds(hiddenColumnIds),
    defaultQueryValues.hidden_columns,
  ),
  column_order: v.optional(
    knownColumnIds(reorderableColumnIds),
    defaultQueryValues.column_order,
  ),
})

const userGroupsQueryParamsSchema = v.object({
  ...paginationSchema.entries,
  ...sortingSchema.entries,
  ...filterSchema.entries,
  ...columnsSchema.entries,
})

export const Route = createFileRoute('/company/_management/groups')({
  head: () => ({
    meta: [
      {
        title: buildPageTitle({
          titleKey: 'page-title-user-groups',
        }),
      },
    ],
  }),
  beforeLoad: ({ context: { user } }) => {
    if (!user?.features.includes(FEATURE_USER_GROUPS)) {
      throw notFound()
    }
  },
  loader: ({ context: { queryClient } }) => {
    queryClient.prefetchQuery(
      userGroupQueries.list({
        page: defaultQueryValues.page,
        size: defaultQueryValues.size,
        name: defaultQueryValues.user_group_name,
        sort_by: defaultQueryValues.sort_by,
        sort_order: defaultQueryValues.sort_order,
        creator_id: defaultQueryValues.creator_id,
        creator_name: defaultQueryValues.creator_name,
        from_created_at: defaultQueryValues.from_created_at,
        to_created_at: defaultQueryValues.to_created_at,
        private: defaultQueryValues.private,
      }),
    )
  },
  component: RouteComponent,
  validateSearch: userGroupsQueryParamsSchema,
  search: {
    middlewares: [stripSearchParams(defaultQueryValues)],
  },
})

function RouteComponent() {
  const hasEnhancedDataTableFeature = useFeatures(FEATURE_ENHANCED_DATA_TABLE)
  return (
    <TableSelectionProvider>
      <CompanyPage
        data-testid="company-groups-page"
        blockScroll={hasEnhancedDataTableFeature}>
        <PageContainer
          py="10"
          flex="1"
          minH="0"
          display="flex"
          flexDirection="column">
          <Suspense
            fallback={
              <Center h="sm">
                <Spinner size="xl" />
              </Center>
            }>
            <UserGroupsList />
          </Suspense>
        </PageContainer>
      </CompanyPage>
      <Outlet />
    </TableSelectionProvider>
  )
}
